import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { dropdownData } from '../../lib/api';
import { useQuery } from 'react-query';
import LoginBar from '../LoginBar';

interface SearchBarProps {
  showBack?: boolean;
}

export const SearchBar = ({ showBack }: SearchBarProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchQuery] = useState<string>(searchParams.get('q') || '');
  const [keywords, setKeywords] = useState<string>('');
  const [topic, setTopic] = useState<string>('');
  const [people, setPeople] = useState<string>('');

  const { data } = useQuery({
    queryKey: ['dropdownData'],
    queryFn: dropdownData,
  });

  const topicData = data ? data['88'] : null;
  const peopleData = data ? data['89'] : null;

  const handleClearForm = () => {
    setKeywords('');
    navigate(`/`);
  };

  useEffect(() => {
    const regex =
      /^(.*?)(?:\speople:([\w\s()+-]+))?(?:\sareaofwork:([\w\s()+-]+))?$/;
    const match = searchQuery.match(regex);

    if (match) {
      const keywordsValue = match[1] || '';
      const peopleValue = match[2] || '';
      const areaOfWorkValue = match[3] || '';

      setKeywords(keywordsValue);
      setPeople(peopleValue);
      setTopic(areaOfWorkValue);
    } else {
      console.log('No match found.');
    }
  }, [searchQuery]);

  const navigateToSearchResults = useCallback(
    (keywords: string, page?: number) => {
      const selectedPage = page || 1;
      navigate(`/search?q=${keywords}&page=${selectedPage}`);
    },
    [navigate]
  );

  const handleSearchClick = useCallback(() => {
    let fullQuery = keywords;

    if (people && people !== 'Select') {
      fullQuery = `${fullQuery}+people:${people}`;
    }

    if (topic && topic !== 'Select') {
      fullQuery = `${fullQuery}+areaofwork:${topic}`;
    }

    navigateToSearchResults(fullQuery, 1);
  }, [keywords, topic, people, navigateToSearchResults]);

  const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    setKeywords(event.target.value);
  };

  const handleTopicChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setTopic(event.target.value);
  };

  const handlePeopleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setPeople(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchClick();
    }
  };

  return (
    <div className="image-library-search-wrapper">
      <div className="image-library-search-inner">
        {showBack && (
          <Link className={'back-link'} to={'/'}>
            Back to Collections
          </Link>
        )}

        <div className="search-fields">
          <div className="field">
            <label>What are you looking for?</label>
            <input
              type="text"
              placeholder="Search for an image"
              value={keywords}
              onChange={handleSearchTextChange}
              onKeyDown={handleKeyDown}
            />
          </div>

          {!!topicData && (
            <div className="field">
              <label>Topic</label>
              <select value={topic} onChange={handleTopicChange}>
                <option>Select</option>
                {topicData.map((item) => (
                  <option key={`topic-option-${item.name}`} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!!peopleData && (
            <div className="field">
              <label>People</label>
              <select value={people} onChange={handlePeopleChange}>
                <option>Select</option>
                {peopleData.map((item) => (
                  <option key={`people-option-${item.name}`} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="search-button-wrapper">
          <div className="search-buttons">
            <button onClick={handleClearForm} className="clearButton">
              Clear
            </button>
            <button onClick={handleSearchClick} className="searchButton">
              Search
            </button>
          </div>
          <LoginBar />
        </div>
      </div>
    </div>
  );
};
